import type { RelativeSelectConfig } from '@lighthouse/core'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { USER_DATASOURCE } from '../../../../../constants'
import { useApplicationContext } from '../../../../../contexts'
import { useAppThemeContext } from '../../../../ApplicationContainer'
import { applyFillPickerValue4CssColorValue } from '../../../../FillPicker'
import type { FieldBaseProps } from '../../../types'
import { RelationCheckboxPreview } from '../RelationCheckboxPreview'
import { RelationRecordPreview } from '../RelationRecordPreview'
import { RelationTagPreview } from '../RelationTagPreview'
import { useRelativeDataController } from '../RelativeDataController'
import { RelativeSelectDrawer } from './RelativeSelectDrawer'

interface RelativeSelectDrawerFieldProps extends FieldBaseProps {
    value?: string
    relativeSelectConfig: RelativeSelectConfig
}
const SCxContainer = styled.div`
    width: 100%;
    height: 100%;
    border-radius: inherit;
`

export const RelativeSelectDrawerField: React.FunctionComponent<RelativeSelectDrawerFieldProps> = props => {
    const {
        id,
        onChange,
        value,
        readOnly,
        isControlled,
        config,
        relativeSelectConfig,
        dataSourceList,
        onFetchDataSource,
        onLoadMoreData,
        onAddClickEvent,
        onRenderLabel
    } = props
    const { theme } = useAppThemeContext()
    const { breakPoint } = config
    const { relativeSelect, placeholder, title = '' } = relativeSelectConfig
    const { relativePointer = '', canMultipleChoice, direction, align, showMode } = relativeSelect ?? {}

    const { pageTarget } = useApplicationContext()

    const [opened, setOpened] = useState(false)
    const { placeholderColor, fontFamily } = useMemo(
        () => ({
            placeholderColor: applyFillPickerValue4CssColorValue(breakPoint?.design?.placeholderColor?.color, theme.palettes),
            fontFamily: breakPoint?.font?.fontFamily
        }),
        [breakPoint?.design?.placeholderColor?.color, breakPoint?.font?.fontFamily, theme.palettes]
    )
    const {
        relativeDataSource: newRelativeDataSource,
        recordValue,
        recordPoints,
        relativeField,
        relativeShowField,
        onRecordChange,
        onChange: onChangeValue
    } = useRelativeDataController({ id, onChange, value, isControlled, config, relativeSelectConfig, dataSourceList })

    const handleRemove = useCallback(
        (recordId: string) => {
            return onRecordChange?.(recordValue.filter(item => item !== recordId))
        },
        [onRecordChange, recordValue]
    )

    return useMemo(() => {
        switch (showMode) {
            case 'checkbox': {
                return (
                    <SCxContainer>
                        <RelationCheckboxPreview
                            dsId={relativePointer}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            breakPoint={breakPoint}
                            recordPoints={recordPoints}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            value={recordValue}
                            disabled={readOnly}
                            onChange={onChangeValue}
                            onRenderLabel={onRenderLabel}
                        />
                    </SCxContainer>
                )
            }
            case 'tag': {
                return (
                    <SCxContainer>
                        <RelationTagPreview
                            dsId={relativePointer}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            recordPoints={recordPoints}
                            isMultiple={canMultipleChoice}
                            direction={direction}
                            align={align}
                            disabled={readOnly}
                            value={recordValue}
                            onChange={onChangeValue}
                        />
                    </SCxContainer>
                )
            }
            default: {
                const userDataSource = find(item => item.id === USER_DATASOURCE, dataSourceList || [])
                return (
                    <SCxContainer
                        data-field-input
                        data-field-active={opened}
                        data-field-disabled={!!readOnly}
                        onKeyDown={ev => ev.stopPropagation()}
                    >
                        <RelationRecordPreview
                            dsId={relativePointer}
                            relativeField={relativeField}
                            relativeShowField={relativeShowField}
                            // recordIds={recordIds}
                            onClick={() => !readOnly && relativeField && relativeShowField && setOpened(true)}
                            userDataSource={userDataSource}
                            placeholder={placeholder}
                            readOnly={readOnly}
                            placeholderColor={placeholderColor}
                            value={recordValue}
                            onRemove={handleRemove}
                        />
                        <RelativeSelectDrawer
                            id={id}
                            value={recordValue}
                            title={title}
                            opened={opened}
                            dsId={relativePointer}
                            target={pageTarget}
                            dataSource={newRelativeDataSource}
                            isMultiple={canMultipleChoice}
                            relativeSelectConfig={relativeSelectConfig}
                            // dataSourceList={dataSourceList}
                            style={{ fontFamily }}
                            onClose={() => setOpened(false)}
                            onChange={onRecordChange}
                            onFetchDataSource={onFetchDataSource}
                            onLoadMoreData={onLoadMoreData}
                            onAddClickEvent={onAddClickEvent}
                        />
                    </SCxContainer>
                )
            }
        }
    }, [
        align,
        breakPoint,
        canMultipleChoice,
        dataSourceList,
        direction,
        fontFamily,
        handleRemove,
        id,
        newRelativeDataSource,
        onAddClickEvent,
        onChangeValue,
        onFetchDataSource,
        onLoadMoreData,
        onRecordChange,
        onRenderLabel,
        opened,
        pageTarget,
        placeholder,
        placeholderColor,
        readOnly,
        recordPoints,
        recordValue,
        relativeField,
        relativePointer,
        relativeSelectConfig,
        relativeShowField,
        showMode,
        title
    ])
}
